import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import * as procService from "../../services/procService";
import * as hrmsService from "../../services/hrmsService";
import { DataTable, ProcBadge, act, amount, quoteKey } from "./procUi";
import { EmptyState, FormDrawer, LoadState, PrimaryButton, SecondaryButton, cardStyle, formatDate, formatDateTime, useLoad } from "../../components/ui";

// Compare & Award (buyer): choose who gets what, send it for approval, follow it to the purchase orders.
//   Lumpsum   the whole RFQ to one quotation
//   Item      each item to one quotation
//   Split     an item shared between quotations (the shares add up to the whole item)
// The server checks every rule again; this screen only collects the choice. Lines and quotations are matched by line number
// and quotation number (ids are encrypted afresh in every answer, so they are never compared).

const AWARDABLE = [2, 13, 11]; // submitted, under evaluation, qualified
const OPEN = ["PENDING_APPROVAL", "APPROVED"];
const STATUS_LABEL = { DRAFT: "Draft", PENDING_APPROVAL: "Waiting for approval", APPROVED: "Approved", REJECTED: "Rejected", WITHDRAWN: "Withdrawn" };
const AwardBadge = ({ status }) => <ProcBadge status={status === "APPROVED" ? "AWARDED" : status === "REJECTED" ? "REJECTED" : status} label={STATUS_LABEL[status]} />;
const priceOf = (q, lineNo) => q.prices.find((p) => p.line_no === lineNo);

function Builder({ rfq, data, draft, onSaved }) {
  const lines = data.lines;
  const quotes = data.quotations.filter((q) => AWARDABLE.includes(q.status));
  const type = rfq.rfq_type;
  const key = (lineNo, qn) => `${lineNo}|${qn}`;

  const [lump, setLump] = useState(() => (draft && type === "LUMPSUM" && draft.lines[0] ? draft.lines[0].quotation_number : null));
  const [item, setItem] = useState(() => (draft && type === "ITEM" ? Object.fromEntries(draft.lines.map((l) => [l.line_no, l.quotation_number])) : {}));
  const [split, setSplit] = useState(() => (draft && type === "SPLIT" ? Object.fromEntries(draft.lines.map((l) => [key(l.line_no, l.quotation_number), String(l.quantity)])) : {}));
  const [busy, setBusy] = useState(false);

  const quoteBy = (qn) => quotes.find((q) => q.quotation_number === qn);

  // what the screen currently means, as the rows the server will take
  const rows = [];
  if (type === "LUMPSUM") {
    const q = quoteBy(lump);
    if (q) lines.forEach((l) => rows.push({ line: l, quote: q, quantity: l.quantity }));
  } else if (type === "ITEM") {
    lines.forEach((l) => {
      const q = quoteBy(item[l.line_no]);
      if (q) rows.push({ line: l, quote: q, quantity: l.quantity });
    });
  } else {
    lines.forEach((l) =>
      quotes.forEach((q) => {
        const n = Number(split[key(l.line_no, q.quotation_number)]);
        if (n > 0 && priceOf(q, l.line_no)) rows.push({ line: l, quote: q, quantity: n });
      })
    );
  }
  const total = rows.reduce((s, r) => s + (priceOf(r.quote, r.line.line_no) ? priceOf(r.quote, r.line.line_no).price : 0) * r.quantity, 0);

  const payload = () =>
    type === "LUMPSUM"
      ? { quotation_id: (quoteBy(lump) || {}).id }
      : { allocations: rows.map((r) => ({ line_id: r.line.id, quotation_id: r.quote.id, ...(type === "SPLIT" ? { quantity: r.quantity } : {}) })) };

  const persist = async () => {
    const res = await procService.saveAward(rfq.id, payload());
    if (!res?.success) {
      toast.error(hrmsService.responseMessage(res));
      return null;
    }
    return res.data;
  };
  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };
  const saveDraft = () =>
    run(async () => {
      const saved = await persist();
      if (saved) {
        toast.success("Draft saved");
        onSaved();
      }
    });
  const send = () =>
    run(async () => {
      const saved = await persist();
      if (!saved) return;
      const res = await procService.submitAward(saved.id);
      if (!res?.success) {
        toast.error(hrmsService.responseMessage(res));
        onSaved();
        return;
      }
      toast.success(res.data && res.data.status === "APPROVED" ? "Award approved: purchase orders created" : res.message || "Award sent for approval");
      onSaved();
    });
  const discard = () => run(async () => act(() => procService.discardAward(draft.id), "Draft discarded").then((r) => r && onSaved()));

  if (!quotes.length) return <EmptyState title="Nothing to award yet" text="An RFQ can be awarded once a supplier has sent a quotation that has not been rejected." />;

  const lowestTotal = Math.min(...quotes.map((q) => q.total));
  return (
    <>
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 12 }}>
        {type === "LUMPSUM" ? "This RFQ is awarded as a whole: choose one quotation." : null}
        {type === "ITEM" ? "This RFQ is awarded item by item: choose a quotation for each item you want to award." : null}
        {type === "SPLIT" ? `This RFQ can be split: share each item between suppliers. A share cannot be less than ${rfq.min_split_pct}% of the item.` : null}
      </div>

      {type === "LUMPSUM"
        ? quotes.map((q) => {
            const complete = q.lines_quoted === lines.length;
            return (
              <label key={q.quotation_number} className="d-flex align-items-start gap-2 mb-2" style={{ ...cardStyle, padding: 12, opacity: complete ? 1 : 0.55, cursor: complete ? "pointer" : "not-allowed" }}>
                <input type="radio" name="lump" className="form-check-input mt-1" disabled={!complete} checked={lump === q.quotation_number} onChange={() => setLump(q.quotation_number)} aria-label={q.company_name} />
                <span style={{ flex: 1 }}>
                  <strong>{q.company_name}</strong> <span style={{ color: "#6B7280", fontSize: 13 }}>{q.quotation_number}</span>{" "}
                  <ProcBadge status={quoteKey(q.status)} label={q.status === 2 ? "Submitted" : undefined} />
                  {q.total === lowestTotal ? <span className="ms-2" style={{ color: "#065F46", fontSize: 12, fontWeight: 700 }}>Lowest total</span> : null}
                  <div style={{ fontSize: 13, color: "#374151" }}>Delivery {q.delivery_time}{q.payment_terms ? ` · Payment ${q.payment_terms}` : ""}{complete ? "" : " · did not price every item"}</div>
                </span>
                <strong>{data.rfq.currency} {amount(q.total)}</strong>
              </label>
            );
          })
        : null}

      {type === "ITEM" ? (
        <DataTable
          minWidth={560}
          rowKey={(l) => l.line_no}
          rows={lines}
          columns={[
            { label: "Item", render: (l) => <><div style={{ fontWeight: 600 }}>{l.item}</div><div style={{ fontSize: 12, color: "#6B7280" }}>{l.quantity} {l.uom || ""}</div></> },
            {
              label: "Award to",
              render: (l) => {
                const offers = quotes.filter((q) => priceOf(q, l.line_no) && priceOf(q, l.line_no).quantity >= l.quantity);
                const best = Math.min(...offers.map((q) => priceOf(q, l.line_no).price));
                return (
                  <select className="form-select" aria-label={`Line ${l.line_no} supplier`} value={item[l.line_no] || ""} onChange={(e) => setItem((s) => ({ ...s, [l.line_no]: e.target.value || undefined }))}>
                    <option value="">Not awarded</option>
                    {offers.map((q) => (
                      <option key={q.quotation_number} value={q.quotation_number}>
                        {q.company_name} · {amount(priceOf(q, l.line_no).price)}{priceOf(q, l.line_no).price === best ? " (lowest)" : ""}
                      </option>
                    ))}
                  </select>
                );
              },
            },
          ]}
        />
      ) : null}

      {type === "SPLIT"
        ? lines.map((l) => {
            const allocated = quotes.reduce((s, q) => s + (Number(split[key(l.line_no, q.quotation_number)]) || 0), 0);
            const touched = allocated > 0;
            const exact = Math.abs(allocated - l.quantity) < 1e-6;
            return (
              <div key={l.line_no} style={{ ...cardStyle, padding: 12, marginBottom: 12 }}>
                <div className="d-flex flex-wrap gap-2 mb-2">
                  <strong>{l.line_no}. {l.item}</strong>
                  <span style={{ color: "#6B7280", fontSize: 13 }}>{l.quantity} {l.uom || ""} wanted</span>
                  <span className="ms-auto" style={{ fontSize: 13, color: !touched ? "#6B7280" : exact ? "#065F46" : "#991B1B", fontWeight: 600 }}>
                    {touched ? `Shared: ${allocated} of ${l.quantity}` : "Not awarded"}
                  </span>
                </div>
                {quotes.filter((q) => priceOf(q, l.line_no)).map((q) => {
                  const p = priceOf(q, l.line_no);
                  return (
                    <div key={q.quotation_number} className="d-flex flex-wrap align-items-center gap-2 mb-1" style={{ fontSize: 14 }}>
                      <span style={{ flex: 1, minWidth: 160 }}>{q.company_name} <span style={{ color: "#6B7280" }}>· {amount(p.price)} each, up to {p.quantity}</span></span>
                      <input type="number" min={0} max={p.quantity} className="form-control" style={{ width: 110 }} aria-label={`Line ${l.line_no} quantity for ${q.company_name}`} value={split[key(l.line_no, q.quotation_number)] || ""} onChange={(e) => setSplit((s) => ({ ...s, [key(l.line_no, q.quotation_number)]: e.target.value }))} />
                    </div>
                  );
                })}
              </div>
            );
          })
        : null}

      <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
        <span style={{ fontSize: 16 }}>Award total <strong>{data.rfq.currency} {amount(total)}</strong></span>
        <span className="ms-auto d-flex gap-2 flex-wrap">
          {draft ? <SecondaryButton onClick={discard} disabled={busy}>Discard draft</SecondaryButton> : null}
          <SecondaryButton onClick={saveDraft} disabled={busy || !rows.length}>Save draft</SecondaryButton>
          <PrimaryButton onClick={send} disabled={busy || !rows.length}>Send for approval</PrimaryButton>
        </span>
      </div>
    </>
  );
}

function Summary({ award, canManage, onChanged }) {
  const [withdrawing, setWithdrawing] = useState(false);
  return (
    <>
      <div style={{ ...cardStyle, padding: 16, marginBottom: 12 }}>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <strong>{award.award_number || "Draft award"}</strong>
          <AwardBadge status={award.status} />
          <strong className="ms-auto">{award.currency} {amount(award.total)}</strong>
        </div>
        <div style={{ fontSize: 13, color: "#6B7280" }}>
          {award.submitted_at ? `Sent ${formatDateTime(award.submitted_at)}` : "Not sent yet"}
          {award.decided_at ? ` · Decided ${formatDate(award.decided_at)}` : ""}
        </div>
        {award.status === "PENDING_APPROVAL" ? (
          <div className="mt-2" style={{ fontSize: 14 }}>
            An approver decides this in <Link to="/approvals">Approvals</Link>. The purchase orders are created once it is approved.
            {canManage ? <> <button type="button" className="btn btn-link p-0 ms-2" onClick={() => setWithdrawing(true)}>Withdraw</button></> : null}
          </div>
        ) : null}
        {award.status === "APPROVED" ? <div className="mt-2" style={{ fontSize: 14 }}>Approved. The purchase orders are under <Link to="/proc/orders">Orders</Link>.</div> : null}
        {award.status === "REJECTED" || award.status === "WITHDRAWN" ? <div className="mt-2" style={{ fontSize: 14, color: "#991B1B" }}>This award was {award.status.toLowerCase()}. You can choose again below.</div> : null}
      </div>
      <DataTable
        minWidth={620}
        rowKey={(l) => l.id}
        rows={award.lines}
        columns={[
          { label: "Item", render: (l) => `${l.line_no}. ${l.item}` },
          { label: "Supplier", render: (l) => <><div>{l.company_name}</div><div style={{ fontSize: 12, color: "#6B7280" }}>{l.quotation_number}</div></> },
          { label: "Quantity", render: (l) => `${l.quantity} ${l.uom || ""}${l.split_pct < 100 ? ` (${l.split_pct}%)` : ""}` },
          { label: "Unit price", render: (l) => amount(l.unit_price) },
          { label: "Amount", render: (l) => amount(l.amount) },
          { label: "Order", render: (l) => l.order_number || "—" },
        ]}
        footer={<tr><th colSpan={4} style={{ padding: "10px 12px" }}>Total ({award.currency})</th><th colSpan={2} style={{ padding: "10px 12px" }}>{amount(award.total)}</th></tr>}
      />
      <FormDrawer
        show={withdrawing}
        title="Withdraw the award"
        submitLabel="Confirm withdrawal"
        initial={{}}
        onHide={() => setWithdrawing(false)}
        onSave={(clean) => procService.withdrawAward(award.id, clean.comment)}
        onDone={onChanged}
        fields={[{ key: "comment", label: "Reason (optional)", type: "textarea", max: 500, hint: "The approvers see this. You can award the RFQ again afterwards." }]}
      />
    </>
  );
}

export default function AwardTab({ rfq, canManage, onChanged }) {
  const awards = useLoad(() => procService.listRfqAwards(rfq.id), [rfq.id, rfq.status]);
  const list = awards.data || [];
  const latest = list[0] || null;
  const latestStatus = latest ? latest.status : null;
  const building = canManage && [1, 13].includes(rfq.status) && (!latest || !OPEN.includes(latestStatus));
  const draftHeader = latestStatus === "DRAFT" ? latest : null;
  const shown = latest && !draftHeader ? latest : null;
  const detail = useLoad(() => procService.getAward((draftHeader || shown).id), [latest ? latest.id : null], { enabled: !!latest });
  const compare = useLoad(() => procService.getRfqQuotations(rfq.id), [rfq.id, rfq.status], { enabled: building });

  const changed = () => {
    awards.reload();
    onChanged();
  };

  if (awards.loading && !awards.data) return <LoadState loading error={null} />;
  return (
    <LoadState loading={false} error={awards.error} onRetry={awards.reload}>
      {shown ? (
        <>
          <h6>{shown.status === "REJECTED" || shown.status === "WITHDRAWN" ? "Previous award" : "Award"}</h6>
          {detail.data && !detail.loading ? <Summary award={detail.data} canManage={canManage} onChanged={changed} /> : <LoadState loading={detail.loading} error={detail.error} onRetry={detail.reload} />}
        </>
      ) : null}
      {building ? (
        <>
          <h6 className={shown ? "mt-4" : ""}>{draftHeader ? "Draft award" : "Award this RFQ"}</h6>
          <LoadState loading={compare.loading || (draftHeader && detail.loading)} error={compare.error} onRetry={compare.reload}>
            {compare.data ? <Builder key={`${rfq.id}:${draftHeader ? draftHeader.id : "new"}:${detail.data ? "d" : ""}`} rfq={rfq} data={compare.data} draft={draftHeader ? detail.data : null} onSaved={changed} /> : null}
          </LoadState>
        </>
      ) : null}
      {!shown && !building ? <EmptyState title="Not awarded" text={rfq.status === 0 ? "An RFQ can be awarded once suppliers have quoted." : rfq.status === 12 ? "This RFQ is closed." : "No award has been made for this RFQ."} /> : null}
      {list.length > 1 ? (
        <>
          <h6 className="mt-4">Earlier awards</h6>
          <DataTable minWidth={420} rowKey={(a) => a.id} rows={list.slice(1)} columns={[{ label: "Award", render: (a) => a.award_number || "Draft" }, { label: "Status", render: (a) => <AwardBadge status={a.status} /> }, { label: "Total", render: (a) => `${a.currency} ${amount(a.total)}` }, { label: "Decided", render: (a) => (a.decided_at ? formatDate(a.decided_at) : "—") }]} />
        </>
      ) : null}
    </LoadState>
  );
}
